import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Select } from 'antd';
import type { InputNode } from '../types';
import { RequiredMark } from './RequiredMark';
import { RTE_SELECT_PLACEMENTS, rteSelectPopupContainer } from '../utils/selectPlacements';

type Props = {
  node: InputNode;
  value: string | undefined;
  onChange: (key: string, value: string) => void;
  disabled?: boolean;
  error?: string;
};

export const SelectNode: React.FC<Props> = React.memo(
  ({ node, value, onChange, disabled, error }) => {
    const handle = useCallback(
      (v: string) => onChange(node.key!, v),
      [node.key, onChange],
    );

    const effective = value ?? node.defaultValue;
    const optionsWithoutNativeTitles = useMemo(
      () => node.options?.map((option) => ({ ...option, title: '' })),
      [node.options],
    );
    const labelText = useMemo(() => {
      if (effective !== undefined && effective !== null && effective !== '') {
        const opt = node.options?.find((o) => String(o.value) === String(effective));
        if (opt) return String(opt.label ?? effective);
        return String(effective);
      }
      return node.placeholder ?? '请选择';
    }, [effective, node.options, node.placeholder]);

    // V1 parity（select/SelectPortal.tsx:52-95）：canvas measureText 实测当前 label 像素宽，
    // 加上 selector 实测 padding + arrow + clear 宽度，下限 36、无上限（长 label 按实际撑开，
    // 不被 antd ellipsis 截断）。与 SearchableSelectNode 的测宽写法一致。
    const [width, setWidth] = useState<number>(64);
    const wrapperRef = useRef<HTMLSpanElement | null>(null);

    const measureSelectWidth = useCallback(() => {
      const wrapper = wrapperRef.current;
      if (!wrapper) return;
      const selectionEl = wrapper.querySelector<HTMLElement>('.ant-select-selection-item');
      const placeholderEl = wrapper.querySelector<HTMLElement>('.ant-select-selection-placeholder');
      const labelEl = selectionEl || placeholderEl;
      const text = (labelEl?.textContent || '').trim() || labelText;
      const selectorEl = wrapper.querySelector<HTMLElement>('.ant-select-selector');
      const fontHost = labelEl || selectorEl || wrapper;
      const cs = window.getComputedStyle(fontHost);
      const fontSize = labelEl ? cs.fontSize : '16px';
      const fontFamily = cs.fontFamily || 'sans-serif';
      const fontWeight = cs.fontWeight || '400';
      const fontStyle = cs.fontStyle || 'normal';
      const font = `${fontStyle} ${fontWeight} ${fontSize} ${fontFamily}`;
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const textWidth = ctx
        ? Math.ceil(((ctx.font = font), ctx.measureText(text).width || 0))
        : 0;
      let padding = 0;
      if (selectorEl) {
        const ss = window.getComputedStyle(selectorEl);
        padding =
          (Number.parseFloat(ss.paddingLeft) || 0) +
          (Number.parseFloat(ss.paddingRight) || 0);
      }
      const arrowEl = wrapper.querySelector<HTMLElement>('.ant-select-arrow');
      const arrowWidth = arrowEl ? Math.ceil(arrowEl.getBoundingClientRect().width) : 12;
      const clearEl = wrapper.querySelector<HTMLElement>('.ant-select-clear');
      let clearWidth = 0;
      if (clearEl) {
        const cs2 = window.getComputedStyle(clearEl);
        if (cs2.display !== 'none' && cs2.visibility !== 'hidden') {
          clearWidth = Math.ceil(clearEl.getBoundingClientRect().width);
        }
      }
      const next = Math.max(36, textWidth + padding + arrowWidth + clearWidth + 6);
      setWidth((prev) => (prev === next ? prev : next));
    }, [labelText]);

    useLayoutEffect(() => {
      const raf = window.requestAnimationFrame(() => measureSelectWidth());
      const timer = window.setTimeout(() => measureSelectWidth(), 60);
      return () => {
        window.cancelAnimationFrame(raf);
        window.clearTimeout(timer);
      };
    }, [measureSelectWidth]);

    useEffect(() => {
      if (!('fonts' in document)) return;
      let cancelled = false;
      const fonts = (document as Document & { fonts?: FontFaceSet }).fonts;
      if (!fonts?.ready) return;
      fonts.ready.then(() => {
        if (!cancelled) measureSelectWidth();
      });
      return () => {
        cancelled = true;
      };
    }, [measureSelectWidth]);

    return (
      <RequiredMark node={node}>
      <span
        ref={wrapperRef}
        className={`rte-select-wrapper${error ? ' rte-select-error' : ''}`}
        data-type="select"
        data-key={node.key}
        style={{ width }}
      >
        <Select
          title=""
          value={effective}
          options={optionsWithoutNativeTitles}
          onChange={handle}
          disabled={disabled}
          placeholder={node.placeholder ?? '请选择'}
          allowClear={!node.required}
          size="small"
          classNames={{ popup: { root: 'rte-select-dropdown' } }}
          popupMatchSelectWidth={false}
          placement="bottomLeft"
          builtinPlacements={RTE_SELECT_PLACEMENTS}
          getPopupContainer={rteSelectPopupContainer}
          suffixIcon={
            <i
              className="iconfont icon-qiehuan-xiayizhang"
              style={{ fontSize: 10, color: '#7C3AED' }}
            />
          }
        />
      </span>
      </RequiredMark>
    );
  },
);
SelectNode.displayName = 'SelectNode';
